import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQueryWithReauth } from './baseQuery';
import { ApiKeyCreateDto, ApiKeyViewDto } from 'src/types/apiKeys';

export const apiKeysAPI = createApi({
    reducerPath: 'apiKeysAPI',
    baseQuery: axiosBaseQueryWithReauth({
        baseUrl: process.env.REACT_APP_API_URL
    }),
    tagTypes: ['ApiKeys', 'GlobalApiKeys'],
    endpoints: (builder) => ({
        getApiKeys: builder.query<ApiKeyViewDto[], { id: number }>({
            query: ({ id }) => ({
                url: `Clients/${id}/ApiKeys`,
                method: 'GET'
            }),
            providesTags: [{
                type: 'ApiKeys',
                id: 'LIST'
            }]
        }),
        createApiKey: builder.mutation<ApiKeyViewDto, { clientId?: number; data: ApiKeyCreateDto }>({
            query: ({ clientId, data }) => ({
                url: `Clients/${clientId}/ApiKeys`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'ApiKeys', id: 'LIST' }],
        }),
        deleteApiKey: builder.mutation<void, { clientId?: number; apiKeyId: number }>({
            query: ({ clientId, apiKeyId }) => ({
                url: `Clients/${clientId}/ApiKeys/${apiKeyId}`,
                method: 'DELETE'
            }),
            async onQueryStarted({ clientId, apiKeyId }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        apiKeysAPI.util.updateQueryData('getApiKeys', { id: clientId }, (draft) => {
                            const index = draft.findIndex(apiKey => apiKey.id === apiKeyId);
                            if (index !== -1) {
                                draft.splice(index, 1);
                            }
                        })
                    );
                } catch {
                }
            }
        }),
        setApiKeyStatus: builder.mutation<boolean, { clientId?: number; apiKeyId: number; status: boolean }>({
            query: ({ clientId, apiKeyId, status }) => ({
                url: `Clients/${clientId}/ApiKeys/${apiKeyId}/Status/${status}`,
                method: 'PATCH'
            }),
            async onQueryStarted({ clientId, apiKeyId, status }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        apiKeysAPI.util.updateQueryData('getApiKeys', { id: clientId }, (draft) => {
                            const apiKey = draft.find(apiKey => apiKey.id === apiKeyId);
                            if (apiKey) {
                                apiKey.active = status;
                            }
                        })
                    );
                } catch {
                }
            }
        }),
        getGlobalApiKeys: builder.query<ApiKeyViewDto[], void>({
            query: () => ({
                url: 'ApiKeys',
                method: 'GET'
            }),
            providesTags: [{
                type: 'GlobalApiKeys',
                id: 'LIST'
            }]
        }),
        createGlobalApiKey: builder.mutation<ApiKeyViewDto, { data: ApiKeyCreateDto }>({
            query: ({ data }) => ({
                url: 'ApiKeys',
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'GlobalApiKeys', id: 'LIST' }],
        }),
        deleteGlobalApiKey: builder.mutation<void, { apiKeyId: number }>({
            query: ({ apiKeyId }) => ({
                url: `ApiKeys/${apiKeyId}`,
                method: 'DELETE'
            }),
            async onQueryStarted({ apiKeyId }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        apiKeysAPI.util.updateQueryData('getGlobalApiKeys', undefined, (draft) => {
                            const index = draft.findIndex(apiKey => apiKey.id === apiKeyId);
                            if (index !== -1) {
                                draft.splice(index, 1);
                            }
                        })
                    );
                } catch {
                }
            }
        }),
        setGlobalApiKeyStatus: builder.mutation<boolean, { apiKeyId: number; status: boolean }>({
            query: ({ apiKeyId, status }) => ({
                url: `ApiKeys/${apiKeyId}/Status/${status}`,
                method: 'PATCH'
            }),
            async onQueryStarted({ apiKeyId, status }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        apiKeysAPI.util.updateQueryData('getGlobalApiKeys', undefined, (draft) => {
                            const apiKey = draft.find(apiKey => apiKey.id === apiKeyId);
                            if (apiKey) {
                                apiKey.active = status;
                            }
                        })
                    );
                } catch {
                }
            }
        }),
    }),
});

export const {
    useGetApiKeysQuery,
    useCreateApiKeyMutation,
    useDeleteApiKeyMutation,
    useSetApiKeyStatusMutation,
    useGetGlobalApiKeysQuery,
    useCreateGlobalApiKeyMutation,
    useDeleteGlobalApiKeyMutation,
    useSetGlobalApiKeyStatusMutation
} = apiKeysAPI;
